// MF entry exposed as ./AppMount. Loads and validates runtime settings before
// the real mount logic (./bootstrap) is ever imported.
import { loadAppSettings, type AppSettings } from "./appSettings";
import type { MountOptions } from "./bootstrap";

// Starts the settings fetch as soon as this MF expose loads (see appSettings.ts).
void loadAppSettings();

// Settings the app can't boot without; validated up front to fail fast with a clear message.
const REQUIRED_SETTINGS = [
  "AUTHENTICATION_AUTHORITY",
  "CLIENT_ID",
  "CLIENT_SCOPE",
  "SETTINGS_API_BASE_URL",
  "COREAPI_BASE_URL",
] as const satisfies ReadonlyArray<keyof AppSettings>;

/** Load settings, validate required keys (throws listing any missing). */
export async function prepareRuntime(action: string): Promise<void> {
  const appSettings = await loadAppSettings();

  const missing = REQUIRED_SETTINGS.filter((key) => {
    const value = appSettings[key];
    return typeof value !== "string" || !value.trim();
  });
  if (missing.length) {
    throw new Error(
      `[admin-ui] cannot ${action}: missing required app settings: ${missing.join(", ")}`,
    );
  }
}

/** Mount into a host-provided element; returns the bootstrap module's unmount fn. */
export const mount = async (args: MountOptions): Promise<() => void> => {
  await prepareRuntime("mount");
  const { mount: appMount } = await import("./bootstrap");
  return appMount(args);
};
